import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { reportsMonthlySignPath } from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import { Textarea } from '@/shared/ui/textarea'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/ui/alert-dialog'
import ReportStatusBadge from '../components/ReportStatusBadge'
import { ReportEmptyState } from '../components/ReportEmptyState'

interface SignerStatus {
  userId: number
  userName: string
  sortOrder: number
  signed: boolean
}

interface PendingReport {
  id: number
  departmentId: number | null
  departmentName: string | null
  siteName: string | null
  reportStartDateFormatted: string
  compliancePercent: number | null
  statusCode: string | null
  signedAt: string | null
  chainSigners: SignerStatus[]
}

interface Props {
  pendingReports: PendingReport[]
  error?: string
  [key: string]: unknown
}

const SignersChain = ({ signers }: { signers: SignerStatus[] }) => (
  <div className="mt-2 space-y-1">
    <p className="text-xs font-medium text-muted-foreground">
      {t('reports.signatures.chainStatus')}
    </p>
    <ol className="space-y-0.5">
      {signers.map((s) => (
        <li key={s.userId} className="flex items-center gap-2 text-sm">
          <span className={s.signed ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
            {s.sortOrder}. {s.userName}
          </span>
          {s.signed && <span className="text-xs text-green-600">✓</span>}
        </li>
      ))}
    </ol>
  </div>
)

const RejectModal = ({ reportId, onClose }: { reportId: number; onClose: () => void }) => {
  const [comments, setComments] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleReject = () => {
    if (!comments.trim()) return
    setSubmitting(true)
    router.post(
      reportsMonthlySignPath({ id: reportId }),
      { is_signed: false, comments: comments.trim() },
      {
        onFinish: () => {
          setSubmitting(false)
          onClose()
        },
      },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-md space-y-4">
        <h3 className="text-lg font-semibold">{t('reports.signatures.reject')}</h3>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor={`reject-reason-${reportId}`}>
            {t('reports.signatures.rejectReason')}
          </label>
          <Textarea
            id={`reject-reason-${reportId}`}
            placeholder={t('reports.signatures.rejectReasonPlaceholder')}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('reports.signatures.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleReject}
            disabled={submitting || !comments.trim()}
          >
            {t('reports.signatures.reject')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const ReportSignatureCard = ({ report }: { report: PendingReport }) => {
  const [rejectOpen, setRejectOpen] = useState(false)

  const handleSign = () => {
    router.post(reportsMonthlySignPath({ id: report.id }), { is_signed: true })
  }

  const complianceLabel = report.compliancePercent != null ? `${report.compliancePercent}%` : '—'

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-5 space-y-3">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold">{report.departmentName ?? '—'}</h3>
          <ReportStatusBadge
            record={{
              statusCode: report.statusCode,
              statusLabel: null,
              isSigned: report.signedAt != null,
            }}
          />
        </div>
        {report.siteName && <p className="text-sm text-muted-foreground">{report.siteName}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span>{report.reportStartDateFormatted}</span>
        <span>
          <span className="font-medium">{t('reports.monthly.compliancePercent')}: </span>
          {complianceLabel}
        </span>
      </div>

      {report.chainSigners.length > 0 && <SignersChain signers={report.chainSigners} />}

      <div className="flex flex-wrap gap-2 pt-1">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm">{t('reports.signatures.sign')}</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('reports.signatures.confirmSignTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('reports.signatures.confirmSign')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('reports.signatures.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleSign}>
                {t('reports.signatures.sign')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button variant="destructive" size="sm" onClick={() => setRejectOpen(true)}>
          {t('reports.signatures.reject')}
        </Button>
      </div>

      {rejectOpen && <RejectModal reportId={report.id} onClose={() => setRejectOpen(false)} />}
    </div>
  )
}

const ReportSignaturesPage = () => {
  const { pendingReports, error } = usePage<Props>().props

  useBreadcrumbs([{ label: t('nav.pendingApprovals') }])

  return (
    <div className="space-y-4 sm:space-y-6">
      <h1 className="text-xl font-bold sm:text-2xl">{t('reports.signatures.title')}</h1>

      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {pendingReports.length === 0 ? (
        <ReportEmptyState message={t('reports.signatures.noPending')} />
      ) : (
        <div className="space-y-4">
          {pendingReports.map((report) => (
            <ReportSignatureCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  )
}

export default ReportSignaturesPage
